/**
 * Telegram WebApp SDK ustidagi tipli qobiq.
 *
 * SDK npm paketi sifatida emas, Telegram serveridan `<script>` orqali
 * yuklanadi (`index.html`) — u har doim TWA versiyasiga mos bo'lishi
 * kerak va bundle hajmiga qo'shilmaydi.
 *
 * Ilova brauzerda ham ochilishi mumkin (ishlab chiqish, vizual test),
 * shuning uchun HAR chaqiruv SDK yo'qligiga chidamli.
 */

export interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: TelegramUser };
  colorScheme?: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  setBackgroundColor?: (color: string) => void;
  setHeaderColor?: (color: string) => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
  HapticFeedback?: { impactOccurred: (style: 'light' | 'medium' | 'heavy') => void };
  /** `t.me/...` havolasini Telegram ichida ochadi (Mini App yopilmaydi). */
  openTelegramLink?: (url: string) => void;

  /**
   * Telegram versiyasi ("7.0"). Eski mijozlarda `undefined`.
   *
   * `requestContact` Bot API 6.9 dan bor, IMZOLANGAN javob esa
   * 8.0 dan. Versiyani tekshirmasdan chaqirilsa eski mijozda
   * usul umuman yo'q va ilova yiqilardi.
   */
  version?: string;

  /**
   * Telegram'ning O'Z ruxsat oynasini ochadi.
   *
   * Mijoz rozi bo'lsa `ok = true` va (8.0+) `payload.response` da
   * IMZOLANGAN yuklama keladi. Imzoni server bot tokeni bilan
   * tekshiradi — raqam klientdan oddiy matn sifatida olinmaydi.
   */
  requestContact?: (
    callback: (ok: boolean, payload?: { status?: string; response?: string }) => void,
  ) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | null {
  return globalThis.window.Telegram?.WebApp ?? null;
}

/**
 * SDK obyekti bormi.
 *
 * DIQQAT: bu "Telegram ichidamiz" degani EMAS. `telegram-web-app.js`
 * oddiy brauzerda ham `window.Telegram.WebApp` ni yaratadi —
 * `initData` esa bo'sh qoladi. Sessiya haqida qaror qabul qilish
 * uchun `telegramSession()` ishlatiladi.
 */
export function hasTelegramSdk(): boolean {
  return webApp() !== null;
}

/**
 * Ilova QAYERDA ochilgani.
 *
 * O'LCHANGAN: brauzerda `{sdkYuklandi: true, webApp: true,
 * initData: ""}` — ya'ni SDK bor, imzo yo'q. Ilova buni "Telegram
 * ichidamiz" deb hisoblab, bo'sh satrni serverga yuborardi va 400
 * olardi; keyin mehmon sifatida davom etardi va har yozish amali
 * sababsiz "Bajarilmadi" berardi.
 *
 * - `inside`  — Telegram ichida, imzolangan `initData` bor
 * - `outside` — SDK bor, lekin imzo yo'q (brauzerda ochilgan)
 * - `absent`  — SDK umuman yuklanmagan (masalan telegram.org yopiq)
 */
export function telegramSession(): 'inside' | 'outside' | 'absent' {
  const app = webApp();
  if (app === null) return 'absent';
  return app.initData === '' ? 'outside' : 'inside';
}

/** Klient yuborgan `initData` — SERVER uni imzo bo'yicha tekshiradi. */
export function initData(): string {
  return webApp()?.initData ?? '';
}

/**
 * Til Telegram'dan AVTOMATIK olinadi, foydalanuvchidan SO'RALMAYDI
 * (docs/02-MAHSULOT.md → "Ikki til").
 */
export function telegramLocale(): 'UZ' | 'RU' {
  const code = webApp()?.initDataUnsafe?.user?.language_code ?? '';
  return code.startsWith('ru') ? 'RU' : 'UZ';
}

/**
 * Ilovani TWA ichida to'g'ri joylashtiradi.
 *
 * `expand()` majburiy: aks holda ilova yarim ekranda ochiladi va
 * pastki tab bar tizim paneli ostida qolib ketadi.
 */
export function initTelegram(backgroundColor: string): void {
  const app = webApp();
  if (app === null) return;

  app.ready();
  app.expand();
  app.setBackgroundColor?.(backgroundColor);
  app.setHeaderColor?.(backgroundColor);
}

/** Telegram'ning o'z orqaga tugmasi — ilovada alohida tugma chizilmaydi. */
export function bindBackButton(handler: (() => void) | null): () => void {
  const button = webApp()?.BackButton;
  if (button === undefined) return () => undefined;

  if (handler === null) {
    button.hide();
    return () => undefined;
  }

  button.onClick(handler);
  button.show();
  return () => {
    button.offClick(handler);
    button.hide();
  };
}

/**
 * `t.me` havolasini ochadi. Telegram ichida `openTelegramLink` (suhbat ilova ichida
 * ochiladi); brauzerda yoki eski mijozda oddiy yangi oyna.
 */
export function openTelegramLink(url: string): void {
  const app = webApp();
  if (app?.openTelegramLink !== undefined) {
    app.openTelegramLink(url);
    return;
  }
  globalThis.window.open(url, '_blank', 'noopener,noreferrer');
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  webApp()?.HapticFeedback?.impactOccurred(style);
}

/**
 * Kuchsiz qurilmani aniqlaydi.
 *
 * O'zbekistondagi trafikning katta qismi past darajadagi Android'da.
 * Aniqlansa `<html data-perf="low">` qo'yiladi va glow o'chadi
 * (docs/03-UI-UX.md → "Ishlash").
 */
export function detectLowEndDevice(): boolean {
  const nav = globalThis.navigator as Navigator & { deviceMemory?: number };
  const memory = nav.deviceMemory;
  const cores = nav.hardwareConcurrency;

  if (typeof memory === 'number' && memory <= 2) return true;
  if (typeof cores === 'number' && cores <= 4) return true;
  return false;
}

/**
 * Telegram'dan telefon raqamini SO'RAYDI.
 *
 * Telegram o'zining ruxsat oynasini ko'rsatadi — biz mijozdan
 * raqamni to'g'ridan-to'g'ri so'ramaymiz va uni majburlamaymiz.
 *
 * QAYTARILADIGAN QIYMAT — IMZOLANGAN YUKLAMA, raqam emas. Raqamni
 * klientdan olib serverga yuborish xavfli bo'lardi: istalgan odam
 * istalgan raqamni o'ziga biriktira olardi. Serverga imzolangan
 * satr boradi va u yerda bot tokeni bilan tekshiriladi.
 *
 * `null` uch holatda qaytadi:
 *   - Telegram mijozi eski (usul yoki imzolangan javob yo'q)
 *   - mijoz rad etdi
 *   - Telegram javobni bermadi
 *
 * Uchalasida ham qo'lda yozish yo'li OCHIQ qoladi — bu tugma
 * qulaylik, yagona yo'l emas.
 */
export async function requestTelegramContact(): Promise<string | null> {
  const app = webApp();
  if (app?.requestContact === undefined) return null;

  // Imzolangan javob Bot API 8.0 dan. Eskisida faqat `true` keladi
  // va undan foydalanib bo'lmaydi.
  const major = Number.parseInt(app.version ?? '0', 10);
  if (Number.isNaN(major) || major < 8) return null;

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    /*
     * Telegram javob bermasa ilova MUZLAB qolmasligi kerak —
     * tugma abadiy "yuklanmoqda" holatida turardi.
     */
    const timer = setTimeout(() => {
      finish(null);
    }, 60_000);

    try {
      app.requestContact?.((ok, payload) => {
        clearTimeout(timer);
        finish(ok && typeof payload?.response === 'string' ? payload.response : null);
      });
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

/** Telegram mijozi imzolangan kontaktni qo'llab-quvvatlaydimi. */
export function canShareContact(): boolean {
  const app = webApp();
  if (app?.requestContact === undefined) return false;
  const major = Number.parseInt(app.version ?? '0', 10);
  return !Number.isNaN(major) && major >= 8;
}
